/**
 * Infinite Scroll for Shopify Collection Pages
 * Smooth animations + scroll position memory on back navigation
 */

(function() {
  'use strict';

  const InfiniteScroll = {
    container: null,
    pagination: null,
    nextPageUrl: null,
    previousPageUrl: null,
    isLoading: false,
    observer: null,
    loadedPages: new Set(),
    currentPage: 1,
    scrollKey: null,
    productSelector: '.collection__page-product',

    init: function() {
      this.container = document.getElementById('main-collection-product-grid');
      this.pagination = document.getElementById('pagination');

      // Must have a product grid container to work
      if (!this.container) return;

      // Include filters and sorting in the key, but not the transient page.
      // This prevents state from one filtered view leaking into another.
      const stateUrl = new URL(window.location.href);
      stateUrl.searchParams.delete('page');
      this.scrollKey = 'infiniteScroll_' +
        stateUrl.pathname +
        '?' +
        stateUrl.searchParams.toString();

      // Shopify may initially render any page (for example after returning
      // from a product while the URL contains ?page=2).
      this.currentPage = parseInt(this.container.dataset.currentPage, 10) ||
        parseInt(new URLSearchParams(window.location.search).get('page'), 10) ||
        1;
      this.loadedPages = new Set([this.currentPage]);

      // Find the pages on either side of the initially rendered page.
      this.updatePaginationUrls();

      // Create loading indicator (must do this before checking nextPageUrl)
      this.createLoadingIndicator();

      // Save scroll position when clicking on products
      this.setupProductClickTracking();

      // Mark existing products as visible
      this.markExistingProductsVisible();

      // Restore any pages from the previous visit before the observer can
      // start requesting more.
      this.restoreScrollPosition().finally(() => {
        this.setupObserver();
      });
    },

    updatePaginationUrls: function() {
      if (!this.pagination) {
        this.nextPageUrl = null;
        this.previousPageUrl = null;
        return;
      }
      const nextLink = this.pagination.querySelector('.paginate_next, .paginext a');
      const previousLink = this.pagination.querySelector('.paginate_prev, .pagiprev a');
      this.nextPageUrl = nextLink ? nextLink.href : null;
      this.previousPageUrl = previousLink ? previousLink.href : null;
    },

    createLoadingIndicator: function() {
      // Create scroll trigger element (invisible element at the bottom)
      const trigger = document.createElement('div');
      trigger.id = 'infinite-scroll-trigger';
      trigger.setAttribute('aria-hidden', 'true');

      const loader = document.createElement('div');
      loader.id = 'infinite-scroll-loader';
      loader.className = 'infinite-scroll-loader';
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-live', 'polite');
      loader.innerHTML = `
        <div class="infinite-scroll-spinner">
          <svg viewBox="0 0 50 50" class="spinner-svg">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
        </div>
          <span class="infinite-scroll-text">Loading more products...</span>
      `;
      loader.style.display = 'none';

      // Loading earlier pages is user-initiated via this button rather than
      // triggered by scrolling up, which was disorienting (products shifting
      // around while the user was just trying to look at what they had).
      const previousButton = document.createElement('button');
      previousButton.type = 'button';
      previousButton.id = 'infinite-scroll-load-previous';
      previousButton.className = 'infinite-scroll-load-previous';
      previousButton.innerHTML = `
        <span class="infinite-scroll-spinner">
          <svg viewBox="0 0 50 50" class="spinner-svg">
            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="infinite-scroll-load-previous__text">Click to load previous products</span>
      `;
      previousButton.addEventListener('click', () => this.loadPreviousPage());
      if (!this.previousPageUrl) {
        previousButton.style.display = 'none';
      }

      this.container.insertAdjacentElement('beforebegin', previousButton);
      this.container.insertAdjacentElement('afterend', trigger);
      trigger.insertAdjacentElement('afterend', loader);

      // Hide the original pagination when infinite scroll is active
      if (this.pagination) {
        this.pagination.style.display = 'none';
      }
    },

    setupObserver: function() {
      const trigger = document.getElementById('infinite-scroll-trigger');

      const options = {
        root: null,
        rootMargin: '400px', // Start loading before user reaches the bottom
        threshold: 0
      };

      if (trigger) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isLoading && this.nextPageUrl) {
              this.loadNextPage();
            }
          });
        }, options);

        this.observer.observe(trigger);
      }
    },

    loadNextPage: function() {
      if (this.isLoading || !this.nextPageUrl) return;

      this.isLoading = true;
      this.showLoader('next');

      // Extract page number from URL
      const urlParams = new URLSearchParams(new URL(this.nextPageUrl).search);
      const pageNum = parseInt(urlParams.get('page')) || this.currentPage + 1;

      fetch(this.nextPageUrl)
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Get new products
          const newProductGrid = doc.getElementById('main-collection-product-grid');
          if (newProductGrid) {
            const newProducts = newProductGrid.querySelectorAll(this.productSelector);
            const existingIds = new Set(
              Array.from(this.container.querySelectorAll('[data-product-id]'))
                .map(product => product.dataset.productId)
            );

            // Add new products with staggered animation
            newProducts.forEach((product, index) => {
              if (product.dataset.productId && existingIds.has(product.dataset.productId)) return;

              // Clone the product
              const clone = product.cloneNode(true);

              // Add animation class
              clone.classList.add(
                'infinite-scroll-item',
                'infinite-scroll-animating',
                'infinite-scroll-hidden'
              );
              clone.style.setProperty('--animation-delay', `${index * 50}ms`);

              // Mark as loaded from page
              clone.dataset.loadedPage = pageNum;

              this.container.appendChild(clone);

              // Trigger animation after a brief delay
              requestAnimationFrame(() => {
                setTimeout(() => {
                  clone.classList.remove('infinite-scroll-hidden');
                  clone.classList.add('infinite-scroll-visible');
                }, 10);
              });

              // Release will-change and transition styles after the staggered
              // entrance has completed.
              setTimeout(() => {
                clone.classList.remove('infinite-scroll-animating');
                clone.style.removeProperty('--animation-delay');
              }, 650 + (index * 50));
            });

            // Re-initialize quickview and other product features
            this.reinitializeProducts();
          }

          // Update next page URL from fetched page
          const newPagination = doc.getElementById('pagination');
          if (newPagination) {
            const nextLink = newPagination.querySelector('.paginate_next, .paginext a');
            this.nextPageUrl = nextLink ? nextLink.href : null;
          } else {
            this.nextPageUrl = null;
          }

          // Track loaded pages
          this.loadedPages.add(pageNum);
          this.currentPage = pageNum;

          // Update URL without reloading (for back button support)
          this.updateUrlState(pageNum);

          // Hide pagination if no more pages
          if (!this.nextPageUrl && this.pagination) {
            this.pagination.style.display = 'none';
            this.showEndMessage();
          }

          this.hideLoader('next');
          this.isLoading = false;

          // Save current state
          this.saveScrollState();
        })
        .catch(error => {
          console.error('Infinite scroll error:', error);
          this.hideLoader('next');
          this.isLoading = false;
          // Show pagination as fallback
          if (this.pagination) {
            this.pagination.style.display = '';
          }
        });
    },

    loadPreviousPage: function() {
      if (this.isLoading || !this.previousPageUrl) return;

      this.isLoading = true;
      this.showLoader('previous');

      const url = this.previousPageUrl;
      const pageNum = parseInt(
        new URL(url).searchParams.get('page'),
        10
      ) || Math.max(1, Math.min(...this.loadedPages) - 1);

      fetch(url)
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.prependProductsFromDocument(doc, pageNum, true);

          const newPagination = doc.getElementById('pagination');
          const previousLink = newPagination &&
            newPagination.querySelector('.paginate_prev, .pagiprev a');
          this.previousPageUrl = previousLink ? previousLink.href : null;

          this.loadedPages.add(pageNum);
          this.reinitializeProducts();
          this.hideLoader('previous');
          this.isLoading = false;
          this.saveScrollState();
        })
        .catch(error => {
          console.error('Infinite scroll error:', error);
          this.hideLoader('previous');
          this.isLoading = false;
        });
    },

    prependProductsFromDocument: function(doc, pageNum, preserveViewport) {
      const newProductGrid = doc.getElementById('main-collection-product-grid');
      if (!newProductGrid) return;

      const firstExistingProduct = this.container.querySelector(this.productSelector);
      const previousTop = firstExistingProduct
        ? firstExistingProduct.getBoundingClientRect().top
        : 0;
      const existingIds = new Set(
        Array.from(this.container.querySelectorAll('[data-product-id]'))
          .map(product => product.dataset.productId)
      );
      const fragment = document.createDocumentFragment();
      const prependedProducts = [];

      newProductGrid.querySelectorAll(this.productSelector).forEach((product) => {
        if (product.dataset.productId && existingIds.has(product.dataset.productId)) return;

        const clone = product.cloneNode(true);
        if (preserveViewport) {
          clone.classList.add(
            'infinite-scroll-item',
            'infinite-scroll-animating',
            'infinite-scroll-hidden'
          );
          prependedProducts.push(clone);
        } else {
          // Session restoration happens before the page is shown and should
          // remain instant.
          clone.classList.add('infinite-scroll-item', 'infinite-scroll-visible');
        }
        clone.dataset.loadedPage = pageNum;
        fragment.appendChild(clone);
      });

      if (!fragment.childNodes.length) return;

      // The user is scrolled to the top of their existing content and just
      // clicked "load previous", so the product that lands right above them
      // (the last one on this earlier page) is what scrolls into view first.
      // The product at the very top of the page they're prepending is what
      // they'd see last, if they keep scrolling up. Stagger the entrance in
      // that order rather than the page's own top-to-bottom order.
      const total = prependedProducts.length;
      const delaysMs = prependedProducts.map((product, index) => {
        const delay = (total - 1 - index) * 50;
        product.style.setProperty('--animation-delay', `${delay}ms`);
        return delay;
      });

      // Disable native scroll anchoring while applying one explicit correction.
      // This prevents browsers from correcting the viewport a second time.
      this.container.classList.add('infinite-scroll-is-prepending');
      this.container.insertBefore(fragment, this.container.firstChild);

      if (preserveViewport && firstExistingProduct) {
        const newTop = firstExistingProduct.getBoundingClientRect().top;
        window.scrollBy(0, newTop - previousTop);
      }

      requestAnimationFrame(() => {
        this.container.classList.remove('infinite-scroll-is-prepending');

        // Use the same staggered entrance as products loaded at the bottom.
        setTimeout(() => {
          prependedProducts.forEach((product, index) => {
            product.classList.remove('infinite-scroll-hidden');
            product.classList.add('infinite-scroll-visible');

            setTimeout(() => {
              product.classList.remove('infinite-scroll-animating');
              product.style.removeProperty('--animation-delay');
            }, 650 + delaysMs[index]);
          });
        }, 10);
      });
    },

    reinitializeProducts: function() {
      // Re-initialize Shopify quickview if available
      if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.quickview) {
        Shopify.theme.quickview.init();
      }

      // Re-initialize product grid video if available
      if (typeof WAU !== 'undefined' && WAU.ProductGridVideo) {
        WAU.ProductGridVideo.init();
      }

      // Re-initialize hover effects for new products
      const newProducts = this.container.querySelectorAll('.infinite-scroll-item');
      newProducts.forEach(product => {
        // Trigger any lazy loading
        const lazyImages = product.querySelectorAll('img[loading="lazy"]');
        lazyImages.forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      });
    },

    updateUrlState: function(pageNum) {
      // Only update if we want to track page in URL (optional)
      // This helps with SEO and allows users to share URLs
      if (pageNum > 1) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', pageNum);
        history.replaceState({ page: pageNum, infiniteScroll: true }, '', url.toString());
      }
    },

    getLoader: function(direction) {
      return document.getElementById(
        direction === 'previous'
          ? 'infinite-scroll-load-previous'
          : 'infinite-scroll-loader'
      );
    },

    showLoader: function(direction) {
      const loader = this.getLoader(direction);
      if (!loader) return;

      clearTimeout(loader.hideTimer);
      clearTimeout(loader.fadeTimer);
      loader.shownAt = Date.now();

      if (direction === 'previous') {
        // Button: disable it and swap its label while the request is in flight.
        loader.disabled = true;
        loader.classList.add('is-loading');
        const text = loader.querySelector('.infinite-scroll-load-previous__text');
        if (text) text.textContent = 'Loading previous products...';
        return;
      }

      loader.style.display = 'flex';
      requestAnimationFrame(() => loader.classList.add('is-loading'));
    },

    hideLoader: function(direction) {
      const loader = this.getLoader(direction);
      if (!loader) return;

      clearTimeout(loader.hideTimer);
      clearTimeout(loader.fadeTimer);

      // Keep fast requests visible long enough for the loading state to be
      // perceived rather than flashing for only a frame.
      const elapsed = Date.now() - (loader.shownAt || 0);
      const remaining = Math.max(0, 400 - elapsed);

      if (direction === 'previous') {
        loader.fadeTimer = setTimeout(() => {
          loader.classList.remove('is-loading');
          loader.disabled = false;
          const text = loader.querySelector('.infinite-scroll-load-previous__text');
          if (text) text.textContent = 'Click to load previous products';
          // Nothing earlier left to load: remove the button entirely.
          loader.style.display = this.previousPageUrl ? '' : 'none';
        }, remaining);
        return;
      }

      loader.fadeTimer = setTimeout(() => {
        loader.classList.remove('is-loading');
        loader.hideTimer = setTimeout(() => {
          loader.style.display = 'none';
        }, 300);
      }, remaining);
    },

    showEndMessage: function() {
      // Only show once
      if (document.querySelector('.infinite-scroll-end')) return;

      const endMessage = document.createElement('div');
      endMessage.className = 'infinite-scroll-end';
      endMessage.innerHTML = '<span>You\'ve viewed all products</span>';

      // Insert after the loader
      const loader = document.getElementById('infinite-scroll-loader');
      if (loader) {
        loader.insertAdjacentElement('afterend', endMessage);
      } else {
        this.container.insertAdjacentElement('afterend', endMessage);
      }
    },

    markExistingProductsVisible: function() {
      const products = this.container.querySelectorAll(this.productSelector);
      products.forEach(product => {
        product.classList.add('infinite-scroll-item', 'infinite-scroll-visible');
        product.dataset.loadedPage = this.currentPage;
      });
    },

    // Scroll Position Memory
    setupProductClickTracking: function() {
      // Track clicks on product links
      this.container.addEventListener('click', (e) => {
        const productLink = e.target.closest('a[href*="/products/"]');
        if (productLink) {
          this.saveScrollState();
        }
      });

      // Also save on beforeunload for other navigation methods
      window.addEventListener('beforeunload', () => {
        this.saveScrollState();
      });
    },

    saveScrollState: function() {
      const visibleProduct = Array.from(
        this.container.querySelectorAll(this.productSelector)
      ).find(product => product.getBoundingClientRect().bottom > 0);
      const visibleRect = visibleProduct && visibleProduct.getBoundingClientRect();
      const scrollData = {
        scrollY: window.scrollY,
        loadedPages: Array.from(this.loadedPages),
        currentPage: this.currentPage,
        timestamp: Date.now(),
        productCount: this.container.querySelectorAll(this.productSelector).length,
        anchorProductId: visibleProduct && visibleProduct.dataset.productId,
        anchorOffset: visibleRect ? visibleRect.top : null
      };

      try {
        sessionStorage.setItem(this.scrollKey, JSON.stringify(scrollData));
      } catch (e) {
        // sessionStorage might be full or disabled
        console.warn('Could not save scroll state:', e);
      }
    },

    restoreScrollPosition: function() {
      try {
        const savedData = sessionStorage.getItem(this.scrollKey);
        if (!savedData) return Promise.resolve();

        const data = JSON.parse(savedData);

        // Only restore if the data is recent (within 30 minutes)
        const maxAge = 30 * 60 * 1000; // 30 minutes
        if (Date.now() - data.timestamp > maxAge) {
          sessionStorage.removeItem(this.scrollKey);
          return Promise.resolve();
        }

        // Check if we're coming back (using performance navigation API)
        const navigationType = performance.getEntriesByType('navigation')[0]?.type;
        const isBackNavigation = navigationType === 'back_forward';

        // Also check if referrer is a product page from same site
        const referrer = document.referrer;
        const isFromProduct = referrer &&
          referrer.includes(window.location.hostname) &&
          referrer.includes('/products/');

        if (isBackNavigation || isFromProduct) {
          return this.reloadPreviousPages(data);
        }
      } catch (e) {
        console.warn('Could not restore scroll state:', e);
      }
      return Promise.resolve();
    },

    reloadPreviousPages: function(data) {
      const pagesToLoad = (data.loadedPages || [])
        .filter(page => page !== this.currentPage)
        .sort((a, b) => a - b);

      if (pagesToLoad.length === 0) {
        return this.scrollToSavedPosition(data);
      }

      this.isLoading = true;

      // Rebuild lower pages from nearest to furthest so prepending keeps the
      // final order correct. Higher pages are appended in ascending order.
      const loadPages = async () => {
        const lowerPages = pagesToLoad
          .filter(page => page < this.currentPage)
          .sort((a, b) => b - a);
        const higherPages = pagesToLoad
          .filter(page => page > this.currentPage)
          .sort((a, b) => a - b);

        for (const pageNum of lowerPages) {
          await this.loadSpecificPage(pageNum, 'previous');
        }
        for (const pageNum of higherPages) {
          await this.loadSpecificPage(pageNum, 'next');
        }

        // After all pages loaded, restore scroll position
        this.isLoading = false;
        await this.scrollToSavedPosition(data);
      };

      return loadPages();
    },

    loadSpecificPage: function(pageNum, direction) {
      return new Promise((resolve) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', pageNum);

        fetch(url.toString())
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newProductGrid = doc.getElementById('main-collection-product-grid');

            if (newProductGrid) {
              if (direction === 'previous') {
                this.prependProductsFromDocument(doc, pageNum, false);
              } else {
                const existingIds = new Set(
                  Array.from(this.container.querySelectorAll('[data-product-id]'))
                    .map(product => product.dataset.productId)
                );
                newProductGrid.querySelectorAll(this.productSelector).forEach(product => {
                  if (product.dataset.productId && existingIds.has(product.dataset.productId)) return;
                  const clone = product.cloneNode(true);
                  clone.classList.add('infinite-scroll-item', 'infinite-scroll-visible');
                  clone.dataset.loadedPage = pageNum;
                  this.container.appendChild(clone);
                });
              }
            }

            // Update the pagination boundary in the direction just loaded.
            const newPagination = doc.getElementById('pagination');
            if (newPagination) {
              if (direction === 'previous') {
                const previousLink = newPagination.querySelector('.paginate_prev, .pagiprev a');
                this.previousPageUrl = previousLink ? previousLink.href : null;
              } else {
                const nextLink = newPagination.querySelector('.paginate_next, .paginext a');
                this.nextPageUrl = nextLink ? nextLink.href : null;
              }
            }

            this.loadedPages.add(pageNum);
            this.currentPage = Math.max(this.currentPage, pageNum);

            // Hide pagination if no more pages
            if (!this.nextPageUrl && this.pagination) {
              this.pagination.style.display = 'none';
            }

            // Reflect whatever pages ended up loaded on the "load previous" button.
            const previousButton = document.getElementById('infinite-scroll-load-previous');
            if (previousButton) {
              previousButton.style.display = this.previousPageUrl ? '' : 'none';
            }

            resolve();
          })
          .catch(() => resolve());
      });
    },

    scrollToSavedPosition: function(data) {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const anchor = data.anchorProductId && Array.from(
              this.container.querySelectorAll('[data-product-id]')
            ).find(product => product.dataset.productId === String(data.anchorProductId));

            if (anchor && typeof data.anchorOffset === 'number') {
              window.scrollBy(0, anchor.getBoundingClientRect().top - data.anchorOffset);
            } else {
              window.scrollTo(0, data.scrollY || 0);
            }

            this.reinitializeProducts();
            sessionStorage.removeItem(this.scrollKey);
            resolve();
          }, 100);
        });
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => InfiniteScroll.init());
  } else {
    InfiniteScroll.init();
  }

  // Expose for external access if needed
  window.ShopifyInfiniteScroll = InfiniteScroll;

})();
